import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import dagLogoDark from '../assets/dag.svg';
import dagLogoLight from '../assets/dag-light.svg';
import { useAppLogout } from '../hooks/useAppLogout';
import DevBadge from './DevBadge';
import { ThemeToggle } from './ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';

export default function DashboardHeader() {
  const { user } = useAuth0();
  const { resolvedTheme } = useTheme();
  const dagLogo = resolvedTheme === 'dark' ? dagLogoDark : dagLogoLight;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const handleLogout = useAppLogout();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  return (
    <header className="dash-header flex flex-shrink-0 items-center pl-8 pr-4 py-[22px]">
      <div className="flex flex-shrink-0 items-center gap-3">
        <Link to="/">
          <img src={dagLogo} alt="DAG" className="h-9 w-auto" />
        </Link>
        <DevBadge />
      </div>
      <div className="ml-auto flex min-w-0 items-center gap-2">
        {user?.email && (
          <div
            className="relative hidden md:flex min-w-0 items-center"
            ref={menuRef}
          >
            <span className="dash-header__welcome hidden text-[13px] sm:inline">
              Welcome,&nbsp;
            </span>
            <button
              onClick={() => setIsMenuOpen((v) => !v)}
              className="dash-header__user-btn min-w-0 max-w-[min(45vw,320px)] font-medium text-[13px]"
            >
              <span className="truncate">{user.name || user.email}</span>
              <ChevronDownIcon
                className={`h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200 ${
                  isMenuOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {isMenuOpen && (
              <div className="dash-header__menu">
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleLogout();
                  }}
                  className="dash-header__menu-item"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
        <ThemeToggle />
      </div>
    </header>
  );
}
